/*
 Copyright (c) 2025. BrightNight. All rights reserved.
 Unauthorized copying of this file, via any medium is strictly prohibited.
 Proprietary and confidential.
 */

import type { AbsoluteURL, FlowRunStatus, PrefectFlow } from '@poweralpha/common';
import { get } from 'lodash';
import { flowNameToLogURL } from './routes';

export const getScenarioLink = (flowName: PrefectFlow | null, scenarioId: string): AbsoluteURL => {
  switch (flowName) {
    case 'PlantSim':
      return `/plant/results?scenario=${scenarioId}`;
    case 'PlantSim Batch':
      return `/plant/batch/results?scenario=${scenarioId}`;
    case 'PlantSim v2':
      return `/plant-v2/results?scenario=${scenarioId}`;
    case 'PVSim simulate energy':
      return `/pv/pvsim/results?scenario=${scenarioId}`;
    case 'PVCosting only':
      return `/pv/costing/simulate?scenario=${scenarioId}`;
    case 'ESS Costing only, Li-ion':
      return `/ess/costing/simulate?scenario=${scenarioId}`;
    default:
      throw new Error(`Could not determine scenario link for flow ${flowName}`);
  }
};

const parseMetStationId = (parameters: Record<string, unknown>) =>
  get(parameters, 'request.met_station_id') as string | undefined;

export const parseProjectIdFromTags = (tags: string[] | undefined) => {
  if (!tags?.length) return undefined;
  return tags.find((tag) => tag.startsWith('sf_project_id='))?.split('=')?.[1];
};

export const getRecordLink = (run: FlowRunStatus, opts?: { scenarioId?: string }): AbsoluteURL => {
  switch (run.flow_name) {
    case 'ESS Only Batch':
    case 'PV Only Batch':
    case 'PV+ESS Batch': {
      const projectId = parseProjectIdFromTags(run.tags);
      return `/projects/${projectId}/batch/${run.id}/`;
    }
    case 'ESS only Project':
    case 'PV only Project':
    case 'PV+ESS Project': {
      const projectId = parseProjectIdFromTags(run.tags);
      return `/projects/${projectId}/results/${opts?.scenarioId}/`;
    }
    case 'PVSim simulate energy':
    case 'PlantSim':
    case 'PlantSim v2':
    case 'ESS Costing only, Li-ion':
    case 'PVCosting only':
      return getScenarioLink(run.flow_name, opts?.scenarioId ?? '');
    case 'PlantSim Batch':
      return '/plant/batch/results';
    case 'PlantSim Net Benefit':
      return `/plant/net-benefit/${run.id}/`;
    case 'PlantSim v2 Net Benefit':
      return '/plant-v2/net-benefit';
    case 'PVSim weather from API':
    case 'Solar Resource Report':
      return '/db/weather';
    case 'Add Price Curves':
      return '/db/prices';
    case 'Energy Scoring': {
      const projectId = parseProjectIdFromTags(run.tags);
      if (projectId) {
        return `/projects/${projectId}/land-scoring`;
      }
      return '/projects';
    }
    case 'METSAT Fleet Report':
      return '/db/met-stations';
    case 'METCAP': {
      const metstationId = parseMetStationId(run.parameters);
      if (metstationId) {
        return `/db/met-stations/${metstationId}/?type=cap`;
      }
      return '/db/met-stations';
    }
    case 'METSAT': {
      const metstationId = parseMetStationId(run.parameters);
      if (metstationId) {
        return `/db/met-stations/${metstationId}/?type=sat`;
      }
      return '/db/met-stations';
    }
    case 'METSOIL': {
      const metstationId = parseMetStationId(run.parameters);
      if (metstationId) {
        return `/db/met-stations/${metstationId}/?type=soil`;
      }
      return '/db/met-stations';
    }
    default:
      throw new Error(`Could not determine record link for flow ${run.flow_name}`);
  }
};

export const getLogsLink = (
  flowName: PrefectFlow | null,
  projectId: string | Array<string> | null | undefined,
): AbsoluteURL => {
  const logLink = flowName ? flowNameToLogURL[flowName] : '/db/logs';
  if (logLink === '/projects' && projectId) {
    const projId = Array.isArray(projectId) ? projectId[0] : projectId;
    return `${logLink}/${projId}/logs`;
  }
  return logLink;
};
